import {
  Box,
  Button,
  type ButtonProps,
  Popover,
  Portal,
  Spinner,
  Text,
  VStack,
  useControllableState
} from "@chakra-ui/react"
import type { IconName } from "@pathwright/pathicons"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { ReactNode, useEffect, useRef } from "react"

export function PublishCardTextButton(props: ButtonProps) {
  return (
    <Button
      variant="plain"
      bg="transparent"
      border="none"
      color="gray.600"
      textDecoration="underline"
      h="auto"
      minW={0}
      p={0}
      fontSize="sm"
      {...props}
    />
  )
}

const ContentPublishCard = ({
  children,
  footer,
  label,
  icon,
  needsPublish,
  busy,
  error,
  open: controlledOpen,
  onOpenChange
}: {
  children: ReactNode
  footer?: ReactNode
  label: string
  icon: IconName
  needsPublish?: boolean
  busy?: boolean
  error?: string | null
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) => {
  const [open, setOpen] = useControllableState({
    value: controlledOpen,
    defaultValue: false,
    onChange: onOpenChange
  })
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hovered = useRef(false)
  const openedByHover = useRef(false)
  const pinned = useRef(false)
  useEffect(() => {
    if (!open) {
      if (timer.current) clearTimeout(timer.current)
      openedByHover.current = false
      pinned.current = false
    }
  }, [open])
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    []
  )
  const hover = (show: boolean) => {
    hovered.current = show
    if (timer.current) clearTimeout(timer.current)
    if (show) {
      if (!open) openedByHover.current = true
      setOpen(true)
    } else if (!pinned.current) {
      timer.current = setTimeout(() => {
        if (!pinned.current) setOpen(false)
      }, 250)
    }
  }
  return (
    <Popover.Root
      open={open}
      onOpenChange={({ open }) => setOpen(open)}
      onFocusOutside={(event) => {
        // Editor reloads can restore focus behind an open card. Keep the card
        // available while the pointer is using it, however it was opened.
        if (hovered.current) event.preventDefault()
      }}
      autoFocus={!openedByHover.current}
      lazyMount
      unmountOnExit={false}
      positioning={{ placement: "bottom-start", flip: false }}
    >
      <Popover.Trigger asChild>
        <Button
          variant="outline"
          colorPalette="gray"
          borderRadius="50px"
          borderColor="gray.400"
          bg="blackAlpha.50"
          _hover={{ bg: "blackAlpha.100" }}
          px={4}
          fontWeight="600"
          lineHeight="1"
          h={9}
          gap={1.5}
          position="relative"
          cursor="pointer"
          opacity={needsPublish === false ? 0.4 : 1}
          disabled={busy}
          onClickCapture={(event) => {
            event.preventDefault()
            const next = openedByHover.current || !open
            openedByHover.current = false
            pinned.current = next
            setOpen(next)
          }}
          onMouseEnter={() => hover(true)}
          onMouseLeave={() => hover(false)}
        >
          <Pathicon icon={icon} size="16px" />
          {label}
          {busy ? (
            <Spinner size="xs" />
          ) : (
            <Pathicon icon="chevron-down" size="12px" />
          )}
          {needsPublish && (
            <Box
              position="absolute"
              top="-2px"
              right="-2px"
              bg="red.500"
              borderRadius="full"
              w="12px"
              h="12px"
            />
          )}
        </Button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            w="max-content"
            maxW="calc(100vw - 24px)"
            aria-label={label}
            css={{ "& p": { marginTop: 0, marginBottom: 0 } }}
            onMouseEnter={() => hover(true)}
            onMouseLeave={() => hover(false)}
            onFocusCapture={() => {
              pinned.current = true
            }}
          >
            <Popover.Arrow>
              <Popover.ArrowTip borderColor="gray.200" />
            </Popover.Arrow>
            <Popover.Body
              hidden={!children && !error}
              p={4}
              maxH="calc(100vh - 220px)"
              overflowY="auto"
              overflowX="auto"
            >
              <VStack align="stretch" gap={4}>
                {children}
                {error && (
                  <VStack
                    role="alert"
                    align="stretch"
                    gap={1}
                    color="red.600"
                    fontSize="sm"
                    css={{ contain: "inline-size" }}
                  >
                    <Text>{error}</Text>
                  </VStack>
                )}
              </VStack>
            </Popover.Body>
            {footer}
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}

export default ContentPublishCard
